// Dictation client — opens the WebSocket relay backed by OpenAI's
// Realtime API transcription. The route is `WS /chat/dictation/ws`; the
// JWT is passed as the `token` query parameter (browser WebSocket
// constructors can't set request headers).
//
// The client receives PCM16 frames from the AudioWorklet via
// `sendAudio(ArrayBuffer)`, base64-encodes them, and sends them as the
// `input_audio_buffer.append` event the Realtime API expects natively.
// Incoming `delta` + `completed` events are surfaced via the
// `onEvent` callback in a small typed envelope; the React component
// owns the rendering logic.

import { supabase } from "./supabase";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export type DictationEvent =
  | { kind: "open" }
  | { kind: "delta"; text: string }
  | { kind: "completed"; text: string }
  | { kind: "error"; code: string }
  | { kind: "close"; code: number; reason: string };

export interface DictationClient {
  sendAudio: (pcm16: ArrayBuffer) => void;
  close: () => void;
}

interface ConnectOptions {
  onEvent: (event: DictationEvent) => void;
}

function toWsUrl(httpBaseUrl: string, token: string): string {
  // VITE_API_BASE_URL is something like `https://api.example.com` (no
  // trailing slash, already stripped above). Same-origin deploys may
  // leave it empty — in that case we build the URL from window.location.
  let origin = httpBaseUrl;
  if (!origin) {
    origin = `${window.location.protocol}//${window.location.host}`;
  }
  const wsOrigin = origin.replace(/^http/, "ws");
  return `${wsOrigin}/chat/dictation/ws?token=${encodeURIComponent(token)}`;
}

function bufferToBase64(buffer: ArrayBuffer): string {
  // `btoa` only accepts string input; we walk the bytes in chunks to
  // avoid blowing the JS argument-count limit on long buffers. ~50 ms
  // frames are well under the limit but we stay generic for safety.
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

export async function connectDictation(options: ConnectOptions): Promise<DictationClient> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new Error("not_authenticated");
  }
  const ws = new WebSocket(toWsUrl(apiBaseUrl, token));

  ws.addEventListener("open", () => {
    options.onEvent({ kind: "open" });
  });

  ws.addEventListener("message", (event) => {
    if (typeof event.data !== "string") {
      return;
    }
    let payload: unknown;
    try {
      payload = JSON.parse(event.data);
    } catch {
      return;
    }
    if (typeof payload !== "object" || payload === null) {
      return;
    }
    const obj = payload as { type?: unknown; delta?: unknown; transcript?: unknown };
    const type = typeof obj.type === "string" ? obj.type : "";
    if (type === "conversation.item.input_audio_transcription.delta") {
      // GA shape: delta events carry the incremental string under `delta`.
      const text = typeof obj.delta === "string" ? obj.delta : "";
      options.onEvent({ kind: "delta", text });
    } else if (type === "conversation.item.input_audio_transcription.completed") {
      // GA shape: completed events carry the canonical text under `transcript`.
      const text = typeof obj.transcript === "string" ? obj.transcript : "";
      options.onEvent({ kind: "completed", text });
    }
  });

  ws.addEventListener("close", (event) => {
    if (event.code !== 1000) {
      options.onEvent({ kind: "error", code: event.reason || `close_${event.code}` });
    }
    options.onEvent({ kind: "close", code: event.code, reason: event.reason });
  });

  return {
    sendAudio: (pcm16: ArrayBuffer) => {
      if (ws.readyState !== WebSocket.OPEN) {
        return;
      }
      const message = JSON.stringify({
        type: "input_audio_buffer.append",
        audio: bufferToBase64(pcm16),
      });
      ws.send(message);
    },
    close: () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, "client_closed");
      }
    },
  };
}

// Exposed for unit tests. Production code uses `connectDictation`.
export const __test_internals = { toWsUrl, bufferToBase64 };
